import { getMediaUrlById } from '@/features/media-library/api';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { TemplatePreview } from './CampaignWizardModal';
import { getCampaign, getTemplate } from './api';

interface Props {
  campanhaId: number;
  onClose: () => void;
}

/**
 * Prévia da mensagem que a campanha enviou — a mesma bolha WhatsApp do wizard,
 * com o template e as variáveis da campanha. Só leitura.
 *
 * Variável de campo (nome, e-mail…) aparece como «rótulo» em azul: o valor real
 * é o de cada destinatário. Em campanha de planilha, colunas da planilha podem
 * ter sobreposto o valor por linha — a prévia mostra o que foi configurado.
 *
 * Diferente do wizard, clicar fora FECHA: aqui não há nada a perder.
 */
export function MensagemDaCampanhaModal({ campanhaId, onClose }: Props) {
  useEscapeClose(true, onClose);

  const campanhaQ = useQuery({
    queryKey: ['campaign', campanhaId],
    queryFn: () => getCampaign(campanhaId),
  });
  const campanha = campanhaQ.data;
  const templateQ = useQuery({
    queryKey: ['template', campanha?.template_id],
    queryFn: () => getTemplate(campanha?.template_id as number),
    enabled: campanha?.template_id != null,
  });
  const midiaId = campanha?.header_media_id ?? null;
  const midiaQ = useQuery({
    queryKey: ['media-url', midiaId],
    queryFn: () => getMediaUrlById(midiaId as number),
    enabled: midiaId != null,
    staleTime: 5 * 60_000,
  });

  const carregando = campanhaQ.isLoading || templateQ.isLoading;
  const falhou = campanhaQ.isError || templateQ.isError;
  const temCampo = Object.values(campanha?.variables_map ?? {}).some((v) => v.kind === 'field');

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click é UX padrão (Esc também fecha)
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-md flex-col rounded-md bg-n-0 shadow-xl">
        <header className="flex items-start justify-between gap-3 border-n-200 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 className="font-bold text-[16px] text-n-900">Mensagem enviada</h2>
            {campanha && <p className="truncate text-[12px] text-n-500">{campanha.name}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-n-500 hover:text-n-700"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto px-5 py-4">
          {carregando && (
            <div className="flex items-center justify-center gap-2 py-10 text-[12px] text-n-500">
              <Loader2 size={14} className="animate-spin" /> Carregando…
            </div>
          )}
          {falhou && (
            <p className="rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              Não foi possível carregar a mensagem desta campanha (o template pode ter sido
              excluído).
            </p>
          )}
          {campanha && templateQ.data && (
            <>
              <TemplatePreview
                template={templateQ.data}
                variables={campanha.variables_map}
                headerMediaOverride={midiaQ.data?.url ?? null}
                headerMediaName={campanha.header_media_name}
              />
              {temCampo && (
                <p className="mt-3 text-[11px] text-n-500">
                  <span className="rounded bg-brand-50 px-1 text-brand-700">«Em azul»</span> é
                  preenchido com o dado de cada destinatário.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
